import { useEffect, useState } from "react";
import { Loader2, Send, Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtTime, STATUS_LABEL } from "@/components/partner/questionsUtil";

function Bubble({ who, text, time, mine }) {
    return (
        <div className={`max-w-[85%] ${mine ? "ml-auto text-right" : ""}`}>
            <p className="text-[0.6rem] tracking-[0.25em] uppercase text-gold">{who}{time ? ` · ${fmtTime(time)}` : ""}</p>
            <p className={`mt-1 inline-block text-left whitespace-pre-wrap text-sm px-4 py-3 rounded-sm ${mine ? "bg-gold/20 text-cream" : "border border-cream/20 text-cream"}`}>{text}</p>
        </div>
    );
}

function QuestionThread({ q, onFlag }) {
    return (
        <li className="space-y-3 py-5 border-b border-cream/10" data-testid={`partner-question-${q.id}`}>
            <Bubble who="You" text={q.question} time={q.created_at} mine />
            {q.ai_answer && <Bubble who="Assistant" text={q.ai_answer} />}
            {q.owner_reply && <Bubble who="Director K" text={q.owner_reply} time={q.replied_at} />}
            <div className="flex flex-wrap items-center gap-3 text-xs text-cream">
                <span data-testid={`partner-question-status-${q.id}`}>{STATUS_LABEL[q.status]}</span>
                {q.status === "ai_answered" && (
                    <button onClick={() => onFlag(q.id)} className="underline inline-flex items-center gap-1" data-testid={`partner-question-flag-${q.id}`}>
                        <Flag size={11} /> Ask Director K instead
                    </button>
                )}
            </div>
        </li>
    );
}

function ReplyEmailSetting() {
    const [email, setEmail] = useState("");
    const [login, setLogin] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/partner/question-email").then(({ data }) => { setEmail(data.email); setLogin(data.login_email); }).catch(() => {});
    }, []);
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/partner/question-email", { email: email.trim() || null });
            setEmail(data.email);
            toast.success("Reply email saved");
        } catch {
            toast.error("Enter a valid email");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="mt-4 p-4 border border-cream/15 rounded-sm" data-testid="question-reply-email-setting">
            <p className="text-sm text-cream">Email me Director K's replies at:</p>
            <div className="mt-2 flex flex-wrap gap-2">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={login}
                    className="flex-1 min-w-[220px] bg-transparent border border-cream/30 rounded-sm px-3 py-2 text-sm text-cream"
                    data-testid="question-reply-email-input" />
                <button onClick={save} disabled={busy} className="btn-pill btn-ghost text-[0.6rem]" data-testid="question-reply-email-save">Save</button>
            </div>
        </div>
    );
}

export default function QuestionsPanel() {
    const [rows, setRows] = useState(null);
    const [text, setText] = useState("");
    const [flag, setFlag] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/partner/questions").then(({ data }) => setRows(data.questions)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);

    const send = async (e) => {
        e.preventDefault();
        if (text.trim().length < 2) return;
        setBusy(true);
        try {
            const { data } = await api.post("/partner/questions", { question: text.trim(), flag });
            setText("");
            setFlag(false);
            toast.success(data.escalated ? "Sent to Director K" : "Answered");
            load();
        } catch {
            toast.error("Could not send — please try again");
        } finally {
            setBusy(false);
        }
    };

    const onFlag = async (id) => {
        try {
            await api.post(`/partner/questions/${id}/flag`);
            toast.success("Sent to Director K");
            load();
        } catch {
            toast.error("Could not send");
        }
    };

    return (
        <div className="max-w-3xl" data-testid="partner-questions-panel">
            <h2 className="font-serif text-3xl text-cream">Questions</h2>
            <p className="mt-2 text-sm text-cream">
                Ask about your agreement or the brand. The assistant answers what it can; anything else goes to Director K, and his reply shows here.
            </p>
            <ReplyEmailSetting />
            <form onSubmit={send} className="mt-6 space-y-3" data-testid="partner-question-form">
                <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your question…"
                    className="w-full bg-transparent border border-cream/30 rounded-sm px-4 py-3 text-sm text-cream"
                    data-testid="partner-question-input" />
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="text-sm text-cream inline-flex items-center gap-2">
                        <input type="checkbox" checked={flag} onChange={(e) => setFlag(e.target.checked)} data-testid="partner-question-flag-checkbox" />
                        Send straight to Director K
                    </label>
                    <button type="submit" disabled={busy || text.trim().length < 2} className="btn-pill btn-primary text-[0.65rem]" data-testid="partner-question-send">
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
                    </button>
                </div>
            </form>
            {!rows ? <Loader2 className="animate-spin mt-8" size={18} /> : (
                <ul className="mt-6" data-testid="partner-question-list">
                    {rows.length === 0 && <p className="text-sm text-cream">No questions yet.</p>}
                    {rows.map((q) => <QuestionThread key={q.id} q={q} onFlag={onFlag} />)}
                </ul>
            )}
        </div>
    );
}
